import { Alert, Button, Card, Group, Stack, Text, Textarea } from '@mantine/core';
import { useState } from 'react';

import { useActivateTemplate, useCreateTemplate, usePromptTemplates } from '../hooks/use-content';
import { useT } from '../i18n';
import { errorMessage } from '../lib/ui';

/** The well-known template name the generator resolves. */
const NAME = 'question_generation';

/**
 * Edit the authoring instruction the generator runs under (PRE-296).
 *
 * This is NOT the template picker PRE-284 removed, and the difference is the
 * reason this exists. A picker asked the operator to choose among variants
 * nobody was comparing — a decision with no basis. This edits the ONE active
 * instruction, which is a different act: it is the tunable half of the prompt,
 * the half the mechanics in code deliberately leave to a person.
 *
 * It lives on the generate screen rather than on a page of its own, because
 * "prompt templates" as a standalone concept is what meant nothing to the
 * reader. Here it has an obvious referent: this is what the button above
 * generates with. Wanting to change it is something that happens while reading
 * what it produced, so it belongs where that is happening.
 *
 * Editing writes a NEW VERSION and activates it; the previous body is never
 * mutated. That is the schema's rule (PRE-73) and it is what makes a batch
 * explicable afterwards — every draft records the template id it was written
 * under, so an edit that silently rewrote the body would leave old drafts
 * pointing at an instruction that no longer says what they were written from.
 *
 * Hidden by default. Generating does not require reading it, and a wall of
 * prose beside the button would suggest it does.
 */
export function AuthoringInstruction() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const t = useT();

  const templates = usePromptTemplates(NAME);
  const create = useCreateTemplate();
  const activate = useActivateTemplate();

  const versions = templates.data?.items ?? [];
  const active = versions.find((t) => t.isActive);
  /** `draft` is null until the operator types, so a refetch cannot clobber edits. */
  const body = draft ?? active?.body ?? '';
  const dirty = active != null && draft != null && draft.trim() !== active.body.trim();
  const saving = create.isPending || activate.isPending;

  async function save() {
    const created = await create.mutateAsync({ name: NAME, body: body.trim() });
    // A new version is inactive unless it is the first, so activation is the
    // second half of "save" rather than a separate thing to remember.
    if (!created.isActive) await activate.mutateAsync(created.id);
    setDraft(null);
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={2}>
          <Text fw={600}>{t('authoring.title')}</Text>
          <Text c="dimmed" size="sm">
            {t('authoring.subtitle')}
          </Text>
        </Stack>
        <Button size="xs" variant="default" onClick={() => setOpen((v) => !v)}>
          {open
            ? t('authoring.hide')
            : active
              ? t('authoring.editVersion', { version: active.version })
              : t('common.edit')}
        </Button>
      </Group>

      {open && (
        <Stack gap="sm" mt="md">
          {templates.isLoading && (
            <Text c="dimmed" size="sm">
              {t('common.loading')}
            </Text>
          )}
          {!templates.isLoading && !active && (
            <Alert color="blue" variant="light">
              {t('authoring.noneYet')}
            </Alert>
          )}
          {active && (
            <>
              <Textarea
                autosize
                minRows={10}
                maxRows={28}
                value={body}
                onChange={(e) => setDraft(e.currentTarget.value)}
                spellCheck={false}
              />
              {(create.isError || activate.isError) && (
                <Text c="red" size="sm">
                  {errorMessage(create.error ?? activate.error)}
                </Text>
              )}
              <Group justify="space-between">
                <Text c="dimmed" size="xs">
                  {t('authoring.saveNote', { version: active.version + 1 })}
                </Text>
                <Group gap="xs">
                  <Button
                    variant="default"
                    size="xs"
                    disabled={!dirty || saving}
                    onClick={() => setDraft(null)}
                  >
                    {t('authoring.revert')}
                  </Button>
                  <Button
                    size="xs"
                    loading={saving}
                    disabled={!dirty || body.trim().length === 0}
                    onClick={() => void save()}
                  >
                    {t('authoring.saveAs', { version: active.version + 1 })}
                  </Button>
                </Group>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Card>
  );
}
